import { Component, OnInit, Input } from '@angular/core'
import { Observable } from 'rxjs'
import { DealConfigurationQuery } from '../../../../../generated/graphql'
import { LocaleService } from '../../../../../framework/locale/services/locale.service'
import { getDealOptionKey, getDealOptionValue } from 'modules/deals/utilities/deal-helpers'
import { FormControl } from '@angular/forms'
import { DealMutationService } from 'modules/deals/services/dealMutation.service'
import { MatDialog } from '@angular/material/dialog'
import { TenantService } from 'framework/tenant/tenant.service'
import { ReferralCelebrationPopupComponent } from '../referral-celebration-popup/referral-celebration-popup.component'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

const orderedCarOptions = ['edition', 'paint', 'rims', 'steeringWheel', 'interior', 'autopilot', 'towhitch']

const orderedContractOptions = [
    'leasingType',
    'mileage',
    'duration',
    'downpayment',
    'deductible',
    'winterTires',
    'premiumConnectivity',
]

interface DealOption {
    key: string
    value: string
}

@UntilDestroy()
@Component({
    selector: 'mg-deal-summary',
    templateUrl: './deal-summary.component.html',
    styleUrls: ['./deal-summary.component.scss'],
})
export class DealSummaryComponent implements OnInit {
    private dealId: string
    isLoading: boolean = true
    carOptions: DealOption[] = []
    contractOptions: DealOption[] = []
    referralInput: FormControl
    isReferralCodeInvalid = false
    hasReferalProgram: boolean

    @Input()
    hideCompanyAndDriver: boolean = false

    @Input()
    withAdjustableReferral: boolean = false

    @Input()
    deal$: Observable<DealConfigurationQuery['Deal']>

    constructor(
        private localeService: LocaleService,
        private tenantService: TenantService,
        private dealMutationService: DealMutationService,
        public dialog: MatDialog
    ) {}

    get locale() {
        return this.localeService.locale
    }

    ngOnInit() {
        this.referralInput = new FormControl('')
        this.hasReferalProgram = this.tenantService.hasReferalProgram$.getValue()

        this.deal$.pipe(untilDestroyed(this)).subscribe(deal => {
            this.isLoading = deal ? false : true

            this.dealId = deal.id

            this.carOptions = []
            this.contractOptions = []

            deal.options.forEach(option => {
                const optionKey = this.getOptionKey(option)
                const optionValue = this.getOptionValue(option)
                const optionObject = {
                    key: this.getOptionKey(option),
                    value: typeof optionValue !== 'boolean' ? optionValue : optionValue ? '✔' : '',
                }

                if (orderedContractOptions.includes(optionKey)) {
                    this.contractOptions = [...this.contractOptions, optionObject]
                } else if (orderedCarOptions.includes(optionKey)) {
                    this.carOptions = [...this.carOptions, optionObject]
                }
            })

            this.contractOptions = this.contractOptions.sort(
                (optA, optB) => orderedContractOptions.indexOf(optA.key) - orderedContractOptions.indexOf(optB.key)
            )
            this.carOptions = this.carOptions.sort(
                (optA, optB) => orderedCarOptions.indexOf(optA.key) - orderedCarOptions.indexOf(optB.key)
            )
        })
    }

    getOptionKey(option) {
        return getDealOptionKey(option)
    }

    getOptionValue(option) {
        return getDealOptionValue(option)
    }

    submitReferralCode() {
        this.dealMutationService.updateDealReferralCode(this.dealId, this.referralInput.value).then(({ data }) => {
            if (!data.updateDealReferralCode || !data.updateDealReferralCode.referralCode) {
                this.isReferralCodeInvalid = true
            } else {
                this.isReferralCodeInvalid = false
                this.openDialog(data.updateDealReferralCode.referralCode)
            }
        })
    }

    openDialog(referralCode: string) {
        const dialogRef = this.dialog.open(ReferralCelebrationPopupComponent)
        dialogRef.componentInstance.referralCode = referralCode
    }
}
